// src/pages/SuperAdmin/AdminFazendas.tsx
import React, { useEffect, useState, useCallback } from "react";
import { fetchFazendas } from "../../services/adminApi";
import { Search, Agriculture, Person } from "@mui/icons-material";

type Fazenda = {
  uid_unidade_producao: string;
  nome: string;
  estado: string;
  codigo_acesso: string;
  area_ha: string | null;
  syn_cadmom: string;
  usuario: { uid_usuario: string; nome: string; telefone: string | null; email: string | null } | null;
};

const formatData = (d: string) =>
  new Date(d).toLocaleDateString("pt-BR", { day: "2-digit", month: "2-digit", year: "numeric" });

const formatTelefone = (t: string | null) => {
  if (!t) return null;
  const d = t.replace(/\D/g, "");
  if (d.length === 13) return `+${d.slice(0, 2)} (${d.slice(2, 4)}) ${d.slice(4, 9)}-${d.slice(9)}`;
  if (d.length === 11) return `(${d.slice(0, 2)}) ${d.slice(2, 7)}-${d.slice(7)}`;
  return t;
};

const AdminFazendas: React.FC = () => {
  const [fazendas, setFazendas] = useState<Fazenda[]>([]);
  const [loading, setLoading] = useState(true);
  const [search, setSearch] = useState("");
  const [debouncedSearch, setDebouncedSearch] = useState("");

  useEffect(() => {
    const t = setTimeout(() => setDebouncedSearch(search), 400);
    return () => clearTimeout(t);
  }, [search]);

  const carregar = useCallback(() => {
    setLoading(true);
    fetchFazendas(debouncedSearch)
      .then(setFazendas)
      .catch(console.error)
      .finally(() => setLoading(false));
  }, [debouncedSearch]);

  useEffect(() => { carregar(); }, [carregar]);

  return (
    <div style={{ padding: "32px 28px", maxWidth: 1100, width: "100%" }}>
      <div style={{ marginBottom: 24 }}>
        <h1 style={{ margin: 0, fontSize: "1.6rem", fontWeight: 700, color: "#1a2e1e" }}>
          Unidades de Produção
        </h1>
        <p style={{ margin: "4px 0 0", color: "#6b7280", fontSize: "0.9rem" }}>
          {loading ? "Carregando..." : `${fazendas.length} fazenda(s) encontrada(s)`}
        </p>
      </div>

      {/* Busca */}
      <div style={{ marginBottom: 20 }}>
        <div style={{
          display: "flex", alignItems: "center", gap: 8,
          background: "#fff", border: "1px solid #d1d5db",
          borderRadius: 10, padding: "8px 14px", maxWidth: 460,
        }}>
          <Search style={{ color: "#9ca3af", fontSize: 20 }} />
          <input
            value={search}
            onChange={(e) => setSearch(e.target.value)}
            placeholder="Buscar por nome, estado ou código..."
            style={{ border: "none", outline: "none", fontSize: "0.9rem", width: "100%", background: "transparent" }}
          />
        </div>
      </div>

      {/* Grid de cards */}
      {loading ? (
        <div style={{ padding: 40, textAlign: "center", color: "#6b7280" }}>Carregando...</div>
      ) : fazendas.length === 0 ? (
        <div style={{ padding: 40, textAlign: "center", color: "#6b7280" }}>Nenhuma fazenda encontrada.</div>
      ) : (
        <div style={{
          display: "grid",
          gridTemplateColumns: "repeat(auto-fill, minmax(300px, 1fr))",
          gap: 16,
        }}>
          {fazendas.map((f) => (
            <div key={f.uid_unidade_producao} style={{
              background: "#fff",
              border: "1px solid #e5ede8",
              borderRadius: 14,
              padding: "18px 20px",
              boxShadow: "0 1px 4px rgba(0,0,0,0.05)",
              transition: "box-shadow 0.18s",
            }}
              onMouseEnter={(e) => (e.currentTarget.style.boxShadow = "0 6px 20px rgba(0,0,0,0.09)")}
              onMouseLeave={(e) => (e.currentTarget.style.boxShadow = "0 1px 4px rgba(0,0,0,0.05)")}
            >
              {/* Header do card */}
              <div style={{ display: "flex", alignItems: "flex-start", gap: 12, marginBottom: 14 }}>
                <div style={{
                  width: 40, height: 40, borderRadius: 10,
                  background: "#e8f4ef", color: "#00b37e",
                  display: "flex", alignItems: "center", justifyContent: "center",
                  flexShrink: 0,
                }}>
                  <Agriculture />
                </div>
                <div style={{ flex: 1, minWidth: 0 }}>
                  <div style={{ fontWeight: 700, fontSize: "0.95rem", color: "#1a2e1e", marginBottom: 2 }}>
                    {f.nome}
                  </div>
                  <div style={{ display: "flex", alignItems: "center", gap: 8 }}>
                    <span style={{
                      background: "#eff6ff", color: "#3b82f6",
                      borderRadius: 6, padding: "2px 8px",
                      fontSize: "0.72rem", fontWeight: 700,
                    }}>{f.estado}</span>
                    <span style={{
                      background: "#f3f4f6", color: "#6b7280",
                      borderRadius: 6, padding: "2px 8px",
                      fontSize: "0.72rem", fontFamily: "monospace",
                    }}>{f.codigo_acesso}</span>
                  </div>
                </div>
              </div>

              {/* Área */}
              {f.area_ha && (
                <div style={{ fontSize: "0.82rem", color: "#6b7280", marginBottom: 10 }}>
                  📐 {f.area_ha} hectares
                </div>
              )}

              {/* Usuário vinculado */}
              <div style={{
                borderTop: "1px solid #f3f4f6", paddingTop: 12, marginTop: 4,
              }}>
                {f.usuario ? (
                  <div style={{ display: "flex", alignItems: "center", gap: 8 }}>
                    <Person style={{ fontSize: 16, color: "#9ca3af" }} />
                    <div>
                      <div style={{ fontSize: "0.85rem", fontWeight: 600, color: "#374151" }}>
                        {f.usuario.nome}
                      </div>
                      <div style={{ fontSize: "0.78rem", color: "#6b7280" }}>
                        {formatTelefone(f.usuario.telefone) || f.usuario.email || "—"}
                      </div>
                    </div>
                  </div>
                ) : (
                  <div style={{ fontSize: "0.82rem", color: "#9ca3af", fontStyle: "italic" }}>
                    Sem usuário vinculado
                  </div>
                )}
              </div>

              {/* Rodapé */}
              <div style={{ fontSize: "0.75rem", color: "#9ca3af", marginTop: 10 }}>
                Cadastrada em {formatData(f.syn_cadmom)}
              </div>
            </div>
          ))}
        </div>
      )}
    </div>
  );
};

export default AdminFazendas;
